import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardHeader, CardBody, Badge, Table, Skeleton } from '../../components/common';
import { reportApi } from '../../services/api';
import './Reports.css';

export default function ContractExpiryReport() {
    const { data, isLoading } = useQuery({
        queryKey: ['report-contract-expiry'],
        queryFn: () => reportApi.getContractExpiry(),
        select: (res) => res.data?.data || res.data || []
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency', currency: 'IDR', minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getDaysUntilExpiry = (endDate) => {
        if (!endDate) return null;
        const days = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
        return days;
    };

    const getExpiryStatus = (days) => {
        if (days === null) return { variant: 'default', label: 'Unknown' };
        if (days < 0) return { variant: 'danger', label: 'Expired' };
        if (days <= 7) return { variant: 'danger', label: 'Critical' };
        if (days <= 30) return { variant: 'warning', label: 'Expiring Soon' };
        if (days <= 90) return { variant: 'info', label: 'Approaching' };
        return { variant: 'success', label: 'Active' };
    };

    const columns = [
        {
            key: 'contractNumber',
            title: 'Contract Number',
            dataIndex: 'contractNumber',
            render: (val) => <span className="contract-number">{val}</span>
        },
        {
            key: 'title',
            title: 'Title',
            dataIndex: 'title',
            render: (val) => <span className="contract-title">{val}</span>
        },
        {
            key: 'vendor',
            title: 'Vendor',
            dataIndex: 'vendor',
            render: (vendor) => vendor?.companyName || vendor?.name || '-'
        },
        {
            key: 'value',
            title: 'Contract Value',
            dataIndex: 'totalValue',
            align: 'right',
            render: (val) => formatCurrency(val)
        },
        {
            key: 'endDate',
            title: 'End Date',
            dataIndex: 'endDate',
            render: (date) => formatDate(date)
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'endDate',
            render: (endDate) => {
                const days = getDaysUntilExpiry(endDate);
                const status = getExpiryStatus(days);
                return (
                    <div className="expiry-status">
                        <Badge variant={status.variant}>{status.label}</Badge>
                        {days !== null && days >= 0 && (
                            <span className="days-remaining">{days} days</span>
                        )}
                    </div>
                );
            }
        }
    ];

    // Calculate summary stats
    const contracts = data || [];
    const expiredCount = contracts.filter(c => getDaysUntilExpiry(c.endDate) < 0).length;
    const criticalCount = contracts.filter(c => {
        const days = getDaysUntilExpiry(c.endDate);
        return days >= 0 && days <= 7;
    }).length;
    const warningCount = contracts.filter(c => {
        const days = getDaysUntilExpiry(c.endDate);
        return days > 7 && days <= 30;
    }).length;

    return (
        <div className="report-page">
            <PageHeader
                title="Contract Expiry Report"
                subtitle="Monitor contracts nearing expiration"
                breadcrumbs={[
                    { label: 'Reports', path: '/reports' },
                    { label: 'Contract Expiry' }
                ]}
            />

            {/* Summary Cards */}
            <div className="report-summary-cards">
                <Card className="summary-card danger">
                    <CardBody>
                        <AlertTriangle size={24} />
                        <div>
                            <span className="summary-value">{expiredCount}</span>
                            <span className="summary-label">Expired</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card warning">
                    <CardBody>
                        <Clock size={24} />
                        <div>
                            <span className="summary-value">{criticalCount}</span>
                            <span className="summary-label">Critical (≤7 days)</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card info">
                    <CardBody>
                        <Calendar size={24} />
                        <div>
                            <span className="summary-value">{warningCount}</span>
                            <span className="summary-label">Expiring Soon (≤30 days)</span>
                        </div>
                    </CardBody>
                </Card>
                <Card className="summary-card success">
                    <CardBody>
                        <CheckCircle size={24} />
                        <div>
                            <span className="summary-value">{contracts.length}</span>
                            <span className="summary-label">Total Contracts</span>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Contract Table */}
            <Card>
                <CardHeader>
                    <h3>Contract Expiry Details</h3>
                </CardHeader>
                <Table
                    columns={columns}
                    data={contracts}
                    loading={isLoading}
                    emptyText="No contracts found"
                    rowKey="id"
                />
            </Card>
        </div>
    );
}
